import AWS from "aws-sdk";
import {Logger} from "@amzn/dolphin-web-framework";
import {getOperationalConfig} from "./network/BaseURLUtils";

/**
 * OperationalAuth — mints an IdToken for the NEW, self-contained operational
 * Cognito user pool (Shepherd eaa16814), used to authenticate the S3 data path.
 *
 * This mirrors the DolphinWebFramework Logger.js mechanism EXACTLY, but uses
 * this account's OWN resources — no katal-logger pool, no cross-account anything:
 *   1. Use GUEST CognitoIdentityCredentials on this account's dedicated
 *      credentials identity pool (credentialsIdentityPoolId) to read the
 *      dummy-user CredentialFile.json from the operational creds bucket.
 *   2. initiateAuth (USER_PASSWORD_AUTH) against the operational user pool
 *      (userPoolId / userPoolClientId) to obtain its IdToken.
 *   3. Cache the token; refresh every 45 minutes (same interval as Logger.js).
 *
 * The resulting token is consumed by S3Client.js and placed in the Logins map
 * keyed by cognito-idp.<region>.amazonaws.com/<operationalUserPoolId>.
 *
 * Known limitation (state in PR): the dummy user is a single SHARED account per
 * realm (mirroring katal-logger). This closes anonymous/guest access to the
 * operational buckets but is not per-associate least privilege; true station
 * scoping needs a real per-associate token plus ${aws:PrincipalTag/stationCode}
 * (follow-up).
 */

const TOKEN_EXPIRATION_MS = 45 * 60 * 1000; // 45 minutes, matching Logger.js

let currentIdToken = null;
let lastTokenUpdate = 0;
let inFlight = null; // de-dupe concurrent refreshes

/**
 * Reads the dummy-user {username, password} from the operational creds bucket
 * using GUEST credentials on this account's credentials identity pool — exactly
 * like Logger.setLogger() reads the katal-logger creds file, but pointed at this
 * account's own credentials pool and bucket.
 */
const readOperationalCredentials = async (operationalConfig) => {
    AWS.config.update({
        region: operationalConfig.region,
        credentials: new AWS.CognitoIdentityCredentials({
            IdentityPoolId: operationalConfig.credentialsIdentityPoolId,
        }),
    });
    const s3 = new AWS.S3();
    const data = await s3.getObject({
        Bucket: operationalConfig.credentialsBucket,
        Key: operationalConfig.credentialsFile,
    }).promise();
    const json = JSON.parse(data.Body.toString());
    return {username: json.username, password: json.password};
};

/**
 * initiateAuth (USER_PASSWORD_AUTH) against the operational user pool.
 * Returns the IdToken. Same shape as Logger.signInUser().
 */
const initiateAuthAgainstOperationalPool = (operationalConfig, username, password) => {
    const cognito = new AWS.CognitoIdentityServiceProvider();
    const params = {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: operationalConfig.userPoolClientId,
        AuthParameters: {
            USERNAME: username,
            PASSWORD: password,
        },
    };
    return new Promise((resolve, reject) => {
        cognito.initiateAuth(params, (err, data) => {
            if (err) {
                reject(err);
            } else {
                resolve(data.AuthenticationResult.IdToken);
            }
        });
    });
};

/**
 * Refreshes the cached operational IdToken. Concurrent callers share one refresh.
 */
const refreshOperationalToken = (operationalConfig) => {
    if (inFlight) {
        return inFlight;
    }
    inFlight = (async () => {
        try {
            const {username, password} = await readOperationalCredentials(operationalConfig);
            const idToken = await initiateAuthAgainstOperationalPool(operationalConfig, username, password);
            currentIdToken = idToken;
            lastTokenUpdate = Date.now();
            return idToken;
        } catch (error) {
            Logger.log.error("OperationalAuth: failed to obtain operational IdToken:", error);
            // Leave currentIdToken as-is (possibly null); caller falls back.
            return currentIdToken;
        } finally {
            inFlight = null;
        }
    })();
    return inFlight;
};

/**
 * Ensures a reasonably fresh operational IdToken exists, minting/refreshing when
 * needed. Call this before building an S3 client. No-ops (returns null) when the
 * operational pool is not configured yet (pre-cutover) so callers fall back to
 * the existing pool.
 *
 * @returns {Promise<string|null>} the IdToken, or null if unavailable.
 */
export const ensureOperationalIdToken = async () => {
    const operationalConfig = getOperationalConfig();
    if (!operationalConfig) {
        return null; // operational pool not configured; fall back to existing pool
    }
    const elapsed = Date.now() - lastTokenUpdate;
    if (!currentIdToken || elapsed >= TOKEN_EXPIRATION_MS) {
        return refreshOperationalToken(operationalConfig);
    }
    return currentIdToken;
};

/**
 * Synchronous read of the last-known operational IdToken (may be null/stale).
 * Used by the synchronous getS3Client() path; freshness is maintained by
 * ensureOperationalIdToken() being awaited on the app's periodic sync loop.
 */
export const getOperationalIdTokenSync = () => currentIdToken;
